import React from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { MessageCircle, Briefcase, Store, Home, Car, User, Gem, GraduationCap, CreditCard, HeartPulse, ArrowRight } from 'lucide-react'

const FinancialServices = () => {
  const whatsappNumber = '917989308337'

  React.useEffect(() => {
    document.title = "Premium Financial Services, Loans & Insurance - Shrijaa Finance";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute("content", "Apply for customized credit products, Business Loans, MSME Loans, Home Loans, Auto Loans, Personal Loans, Gold Loans, Education Loans, Credit Cards, and General/Life Insurance with Shrijaa Finance.");
    }

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', window.location.origin + '/financial-services');

    const schemaId = 'finance-jsonld-schema';
    let script = document.getElementById(schemaId);
    if (!script) {
      script = document.createElement('script');
      script.id = schemaId;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    
    const financeSchema = {
      "@context": "https://schema.org",
      "@type": "Service",
      "serviceType": "Financial Services",
      "provider": {
        "@type": "Organization",
        "name": "Shrijaa Outsourcing Consultants",
        "url": "https://shrijaaoutsourcing.com"
      },
      "areaServed": "IN",
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Financial Products Catalog",
        "itemListElement": financialServices.map((service, idx) => ({
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": service.title,
            "description": `Customized ${service.title} schemes including: ${service.offerings.join(', ')}`
          }
        }))
      }
    };
    
    script.textContent = JSON.stringify(financeSchema);
    
    return () => {
      const activeScript = document.getElementById(schemaId);
      if (activeScript) {
        activeScript.remove();
      }
    };
  }, []);

  const financialServices = [
    {
      icon: <Briefcase className="w-8 h-8" />,
      title: 'Business Loans',
      offerings: ['Working Capital', 'Term Loans', 'Overdraft', 'Project Finance'],
      color: 'from-blue-600 to-indigo-700',
      image: '/service-business-loan.webp'
    },
    {
      icon: <Store className="w-8 h-8" />,
      title: 'MSME Loans',
      offerings: ['Business Expansion', 'Machinery Finance', 'Working Capital', 'Government-linked Schemes'],
      color: 'from-emerald-600 to-teal-700',
      image: '/service-msme-loan.webp'
    },
    {
      icon: <Home className="w-8 h-8" />,
      title: 'Home Loans',
      offerings: ['Purchase', 'Construction', 'Balance Transfer', 'Top-up Loans'],
      color: 'from-orange-500 to-amber-600',
      image: '/service-home-loan.webp'
    },
    {
      icon: <Car className="w-8 h-8" />,
      title: 'Auto Loans',
      offerings: ['New Vehicles', 'Used Vehicles', 'Commercial Vehicles', 'Construction Equipment', 'Tractor'],
      color: 'from-rose-500 to-red-600',
      image: '/service-auto-loan.webp'
    },
    {
      icon: <User className="w-8 h-8" />,
      title: 'Personal Loans',
      offerings: ['Salaried', 'Self-employed'],
      color: 'from-purple-600 to-violet-700',
      image: '/service-personal-loan.webp'
    },
    {
      icon: <Gem className="w-8 h-8" />,
      title: 'Gold Loans',
      offerings: ['Quick Processing', 'High Loan Value', 'Competitive Interest Rates'],
      color: 'from-amber-500 to-yellow-600',
      image: '/service-gold-loan.webp'
    },
    {
      icon: <GraduationCap className="w-8 h-8" />,
      title: 'Education Loans',
      offerings: ['Domestic', 'International', 'Higher Education'],
      color: 'from-cyan-600 to-blue-700',
      image: '/service-education-loan.webp'
    },
    {
      icon: <CreditCard className="w-8 h-8" />,
      title: 'Credit Cards',
      offerings: ['Premium Cards', 'Travel Cards', 'Reward Cards'],
      color: 'from-pink-600 to-rose-700',
      image: '/service-credit-card.webp'
    },
    {
      icon: <HeartPulse className="w-8 h-8" />,
      title: 'Insurance',
      offerings: ['Life – Term / Whole Life', 'General – Motor / Health / Property / Travel'],
      color: 'from-teal-600 to-emerald-700',
      image: '/service-insurance.webp'
    }
  ]

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-800 via-primary-700 to-teal-700 text-white py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,rgba(20,184,166,0.1),transparent)]"></div>
        <div className="container-max text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-accent-400 font-bold uppercase tracking-wider text-sm px-4 py-1.5 bg-white/10 rounded-full backdrop-blur-sm">
              Shrijaa Finance
            </span>
            <h1 className="text-4xl md:text-6xl font-black mt-6 mb-6 tracking-tight">
              Premium Financial Services
            </h1>
            <p className="text-xl text-primary-100 max-w-2xl mx-auto font-light leading-relaxed">
              Empowering individuals and organizations with tailored credit facilities, loans, credit cards, and comprehensive insurance solutions.
            </p>
            <div className="mt-8 flex justify-center gap-4">
              <Link
                to="/financial-services/apply"
                className="bg-accent-500 hover:bg-accent-600 text-white font-bold py-3.5 px-8 rounded-xl shadow-lg transition-all transform hover:-translate-y-0.5 inline-flex items-center gap-2"
              >
                Apply Online Now
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Grid Section */}
      <section className="section-padding bg-gradient-to-b from-secondary-50 to-white relative overflow-hidden">
        {/* Decorative background vectors */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary-100/50 rounded-full filter blur-3xl opacity-30 -translate-y-12 translate-x-12"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-teal-100/50 rounded-full filter blur-3xl opacity-30 translate-y-12 -translate-x-12"></div>

        <div className="container-max relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {financialServices.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.05 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl shadow-md hover:shadow-2xl transition-all duration-300 overflow-hidden border border-secondary-100/80 group flex flex-col justify-between"
              >
                <div>
                  {/* Top Header color strip */}
                  <div className={`h-2 bg-gradient-to-r ${service.color}`} />
                  
                  {/* Category Image */}
                  <div className="h-44 overflow-hidden relative">
                    <img
                      src={service.image}
                      alt={service.title}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-secondary-900/40 via-transparent to-transparent" />
                  </div>
                  
                  <div className="p-8">
                    <div className="flex items-center gap-4 mb-6">
                      <div className={`p-3 rounded-xl bg-gradient-to-br ${service.color} text-white shadow-md group-hover:scale-110 transition-transform duration-300`}>
                        {service.icon}
                      </div>
                      <h3 className="text-2xl font-bold text-secondary-900 group-hover:text-primary-600 transition-colors">
                        {service.title}
                      </h3>
                    </div>

                    <ul className="space-y-3 mb-6">
                      {service.offerings.map((offering, idx) => (
                        <li key={idx} className="flex items-start text-secondary-700 text-[15px]">
                          <span className={`inline-block w-1.5 h-1.5 rounded-full mt-2 mr-3 bg-gradient-to-r ${service.color}`} />
                          <span className="font-medium">{offering}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                 <div className="px-8 pb-8">
                  <Link
                    to="/financial-services/apply"
                    state={{ category: service.title }}
                    className={`w-full py-3 px-4 rounded-xl font-semibold text-center text-white bg-gradient-to-r ${service.color} hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2`}
                  >
                    <ArrowRight className="w-5 h-5" />
                    Enquire Now
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-primary-600 text-white text-center relative overflow-hidden">
        <div className="container-max relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Need Expert Financial Guidance?
          </h2>
          <p className="text-xl text-primary-100 mb-8 max-w-2xl mx-auto">
            Get in touch with our certified financial consultants to find the best solutions for your personal or business needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/financial-services/apply"
              className="bg-white text-primary-600 hover:bg-primary-50 font-semibold py-3 px-8 rounded-lg transition-colors duration-200 inline-flex items-center justify-center"
            >
              Apply Online Now
            </Link>
            <a
              href={`https://wa.me/${whatsappNumber}?text=${encodeURIComponent("Hello! I would like to schedule a free financial consultation with Shrijaa Outsourcing Consultants.")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="border-2 border-white text-white hover:bg-white hover:text-primary-600 font-semibold py-3 px-8 rounded-lg transition-colors duration-200"
            >
              WhatsApp Consultation
            </a>
          </div>
        </div>
      </section>
    </div>
  )
}

export default FinancialServices
